/* global theme */

/**
 * Owns every piece of discount UI from one cart state: the header badge, the cart drawer
 * notice bar, and the one-time toast.
 *
 * Liquid's cart.discount_codes is empty for codes attached by a /discount/ URL on this
 * store, so the state is read from the AJAX cart instead. It is refreshed on the theme's
 * own cart events - both bubble to the document:
 *   on:cart:add        product-form.js:125  (a product not already in the cart)
 *   on:line-item:change product-form.js:115 (a product already in the cart)
 *                       cart-items.js:139   (quantity change or remove, drawer and page)
 */
(() => {
  const CONFIG = document.currentScript;

  const STORAGE_KEY = 'rg-discount-toast';
  const TOAST_VISIBLE_MS = 8000;
  const TOAST_FADE_MS = 300;
  const EDGE_GAP = 12;

  const text = {
    toast: CONFIG?.dataset.toastText || '',
    badge: CONFIG?.dataset.badgeText || '',
    below: CONFIG?.dataset.textBelow || '',
    met: CONFIG?.dataset.textMet || ''
  };

  /**
   * Whether the toast has already been shown this session. Treats an unavailable
   * sessionStorage (private browsing, blocked cookies) as "already shown", so the toast is
   * never shown repeatedly when it cannot be remembered.
   * @returns {boolean}
   */
  function toastAlreadyShown() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch (error) {
      return true;
    }
  }

  /**
   * Records that the toast has been shown this session.
   */
  function markToastShown() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      // Storage unavailable - the toast simply shows once per page instead.
    }
  }

  /**
   * Reads the cart and reduces it to the discount state the UI needs.
   * @returns {Promise<{attached: boolean, applicable: boolean, code: string, itemCount: number}>}
   */
  async function readState() {
    const empty = { attached: false, applicable: false, code: '', itemCount: 0 };
    const cartRoute = window.theme?.routes?.cart || '/cart';

    try {
      const response = await fetch(`${cartRoute}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return empty;

      const cart = await response.json();
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      if (codes.length === 0) return empty;

      const first = codes[0] || {};

      return {
        attached: true,
        itemCount: Number(cart.item_count) || 0,
        // Shopify omits `applicable` on some cart payloads. Absent is treated as not yet
        // applicable, so the wording never promises a discount the cart has not earned.
        applicable: first.applicable === true,
        code: typeof first.code === 'string' ? first.code : ''
      };
    } catch (error) {
      return empty;
    }
  }

  /**
   * Fills every header badge span. There can be more than one: the cart icon bubble is
   * re-rendered by the Section Rendering API, and the replacement carries its own span.
   * @param {object} state - Discount state from readState().
   */
  function renderBadges(state) {
    document.querySelectorAll('.c-discount-badge').forEach((badge) => {
      badge.textContent = state.attached ? text.badge : '';
      badge.hidden = !state.attached;
    });
  }

  /**
   * Fills the cart drawer notice bar.
   * @param {object} state - Discount state from readState().
   */
  function renderNotice(state) {
    const wrapper = document.getElementById('c-discount-notice');
    if (!wrapper) return;

    // No notice over an empty drawer. This is the only guard: the #c-discount-notice
    // wrapper renders unconditionally so there is a mount point when an item is added to
    // an empty cart without a page reload, and emptying the cart from inside the drawer
    // re-renders only cart-items, promoted products, the summary and the media promotion,
    // never this wrapper.
    if (!state.attached || state.itemCount < 1) {
      wrapper.textContent = '';
      return;
    }

    const message = state.applicable
      ? text.met
      : text.below.replace('{code}', state.code);

    if (!message) {
      wrapper.textContent = '';
      return;
    }

    const bar = document.createElement('div');
    bar.className = 'c-discount-notice__bar';
    bar.setAttribute('role', 'status');

    // {br} in the setting text forces a line break, so a two-sentence notice reads as two
    // lines instead of wrapping wherever the drawer width happens to fall. Same token style
    // as {code}; both are typed into the header's "Discount messaging" settings.
    const body = document.createElement('div');
    body.className = 'c-discount-notice__text';
    message.split('{br}').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const span = document.createElement('span');
      span.className = 'c-discount-notice__line';
      span.textContent = trimmed;
      body.appendChild(span);
    });
    bar.appendChild(body);

    // The confirmed icon marks the applicable state only. An attached code that does not
    // yet apply gets the text alone, so the tick never promises a discount the cart has not
    // earned. The SVG lives in snippets/c-discount-notice.liquid as a <template>, outside
    // the wrapper this function empties, so it survives every re-render.
    const iconTemplate = state.applicable ? document.getElementById('c-discount-notice-icon') : null;
    if (iconTemplate && iconTemplate.content) {
      const icon = document.createElement('span');
      icon.className = 'c-discount-notice__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.appendChild(iconTemplate.content.cloneNode(true));
      bar.appendChild(icon);
    }

    wrapper.textContent = '';
    wrapper.appendChild(bar);
  }

  /**
   * Positions the toast under the cart icon, clamped inside the viewport, and points the
   * notch back at the centre of the icon.
   * @param {HTMLElement} toast - The toast element.
   * @param {HTMLElement} icon - The header cart icon.
   */
  function positionToast(toast, icon) {
    const rect = icon.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    const width = toast.offsetWidth;
    const iconCentre = rect.left + (rect.width / 2);
    const rightLimit = Math.max(EDGE_GAP, viewport - width - EDGE_GAP);
    const left = Math.min(Math.max(iconCentre - (width / 2), EDGE_GAP), rightLimit);
    const notch = Math.min(Math.max(iconCentre - left, 16), Math.max(16, width - 16));

    toast.style.top = `${rect.bottom + 10}px`;
    toast.style.left = `${left}px`;
    toast.style.setProperty('--c-discount-toast-notch-left', `${notch}px`);
  }

  /**
   * Builds, shows and schedules the removal of the toast.
   * @param {HTMLElement} icon - The header cart icon to anchor to.
   */
  function showToast(icon) {
    const toast = document.createElement('div');
    toast.className = 'c-discount-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const notch = document.createElement('span');
    notch.className = 'c-discount-toast__notch';
    notch.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'c-discount-toast__text';
    label.textContent = text.toast;

    toast.append(notch, label);
    document.body.appendChild(toast);
    positionToast(toast, icon);

    let timer = null;
    let tracking = false;

    // The toast is fixed to the viewport but anchored to the cart icon, and the sticky
    // header moves the icon as the page scrolls, so its position is recomputed for as
    // long as the toast is on screen rather than only when it is created.
    const trackIcon = () => {
      if (tracking) return;
      tracking = true;

      window.requestAnimationFrame(() => {
        tracking = false;
        if (toast.isConnected) positionToast(toast, icon);
      });
    };

    const dismiss = () => {
      if (!toast.isConnected) return;
      clearTimeout(timer);
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('scroll', trackIcon);
      window.removeEventListener('resize', trackIcon);
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), TOAST_FADE_MS);
    };

    function onKeydown(evt) {
      if (evt.key === 'Escape') dismiss();
    }

    toast.addEventListener('click', dismiss);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('scroll', trackIcon, { passive: true });
    window.addEventListener('resize', trackIcon);

    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    timer = setTimeout(dismiss, TOAST_VISIBLE_MS);

    markToastShown();
  }

  /**
   * Reads the cart and paints all three pieces of UI.
   * @param {boolean} [allowToast=false] - Whether the toast may be shown on this pass.
   */
  async function refresh(allowToast = false) {
    const state = await readState();

    renderBadges(state);
    renderNotice(state);

    if (!allowToast || !state.attached || !text.toast || toastAlreadyShown()) return;

    const icon = document.getElementById('cart-icon');
    if (icon) showToast(icon);
  }

  function init() {
    refresh(true);

    // Both events bubble to the document, and both are dispatched after the theme has
    // finished re-rendering its cart sections, so the cart read here sees the new state.
    document.addEventListener('on:cart:add', () => refresh(false));
    document.addEventListener('on:line-item:change', () => refresh(false));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
